// ABOUTME: Modal wizard for automated Shopify connector onboarding (create) and editing existing connectors.
// ABOUTME: Create mode is a 4-step wizard: discover shop → configure scopes → automate app creation → install links.

import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ShoppingBag,
  Copy,
  CheckCircle,
  Search,
  Loader2,
  XCircle,
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  KeyRound,
  Globe,
  Cookie,
  RefreshCw,
  Upload,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { shopifyConnectorApi } from '@/services/api/adminClient';
import type {
  ShopifyConnector,
  ShopifyConnectorUpdateRequest,
  ShopifyAutomateAppCreationResponse,
  ShopifyAutomationStepResult,
} from '@/types/admin';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

interface ShopifyConnectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  connector?: ShopifyConnector;
  onSaved: (connector: ShopifyConnector) => void;
}

const AVAILABLE_RESOURCES = [
  { value: 'products', label: 'Products' },
  { value: 'orders', label: 'Orders' },
  { value: 'customers', label: 'Customers' },
];

const FREQUENCY_OPTIONS = [
  { value: 60, label: 'Every hour' },
  { value: 360, label: 'Every 6 hours' },
  { value: 720, label: 'Every 12 hours' },
  { value: 1440, label: 'Every 24 hours' },
];

type WizardStep = 1 | 2 | 3 | 4;

// ---------------------------------------------------------------------------
// Shopify OAuth Scopes — grouped by category
// ---------------------------------------------------------------------------

interface ScopeCategory {
  name: string;
  scopes: string[];
}

const SCOPE_CATEGORIES: ScopeCategory[] = [
  {
    name: 'Orders & Fulfillment',
    scopes: [
      'read_orders', 'read_order_edits', 'read_draft_orders', 'read_checkouts',
      'read_fulfillments', 'read_assigned_fulfillment_orders',
      'read_merchant_managed_fulfillment_orders', 'read_third_party_fulfillment_orders',
      'read_custom_fulfillment_services', 'read_fulfillment_constraint_rules',
      'read_shipping', 'read_delivery_customizations', 'read_delivery_option_generators',
      'read_returns', 'write_packing_slip_templates',
    ],
  },
  {
    name: 'Products & Collections',
    scopes: [
      'read_products', 'read_product_feeds', 'read_product_listings', 'read_publications',
      'read_price_rules', 'read_discounts', 'read_discounts_allocator_functions',
      'read_purchase_options',
    ],
  },
  {
    name: 'Customers',
    scopes: [
      'read_customers', 'read_customer_merge', 'read_customer_events',
      'read_custom_pixels', 'read_pixels', 'read_companies',
    ],
  },
  {
    name: 'Inventory & Locations',
    scopes: [
      'read_inventory', 'read_inventory_shipments',
      'read_inventory_shipments_received_items', 'read_inventory_transfers',
      'read_locations',
    ],
  },
  {
    name: 'Content & Store',
    scopes: [
      'read_content', 'read_online_store_pages', 'read_online_store_navigation',
      'read_themes', 'read_locales', 'read_translations', 'read_files',
      'read_metaobjects', 'read_metaobject_definitions',
      'read_legal_policies', 'read_privacy_settings',
      'read_channels', 'read_app_proxy',
    ],
  },
  {
    name: 'Marketing & Analytics',
    scopes: [
      'read_analytics', 'read_marketing_events', 'read_marketing_integrated_campaigns',
      'read_reports', 'write_reports', 'read_resource_feedbacks',
    ],
  },
  {
    name: 'Payments & Billing',
    scopes: [
      'read_payment_terms', 'read_payment_customizations',
      'read_gift_cards', 'read_gift_card_transactions',
      'read_shopify_payments_payouts', 'read_shopify_payments_disputes',
      'read_store_credit_accounts', 'read_store_credit_account_transactions',
      'read_cash_tracking',
    ],
  },
  {
    name: 'Customer Account API',
    scopes: [
      'customer_read_companies', 'customer_read_customers',
      'customer_read_draft_orders', 'customer_read_markets',
      'customer_read_metaobjects', 'customer_read_orders',
      'customer_read_quick_sale',
      'customer_read_store_credit_account_transactions', 'customer_read_store_credit_accounts',
    ],
  },
  {
    name: 'Other',
    scopes: [
      'read_markets', 'read_markets_home',
      'read_cart_transforms', 'read_all_cart_transforms',
      'read_checkout_branding_settings', 'read_validations',
      'read_script_tags', 'read_audit_events', 'read_discovery',
    ],
  },
];

const ALL_SCOPES = SCOPE_CATEGORIES.flatMap((c) => c.scopes);

// ---------------------------------------------------------------------------
// Automation step definitions for SSE streaming
// ---------------------------------------------------------------------------

/** Step definition with display name and SSE event key */
const AUTOMATION_STEP_DEFS = [
  { key: 'login', label: 'Logging in' },
  { key: 'create_app', label: 'Creating app' },
  { key: 'configure_and_release', label: 'Configuring & publishing' },
  { key: 'extract_credentials', label: 'Extracting credentials' },
  { key: 'set_distribution', label: 'Generating install link' },
] as const;

type StepStatus = 'pending' | 'running' | 'success' | 'failed';

interface LiveStep {
  key: string;
  label: string;
  status: StepStatus;
  detail?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ShopifyConnectorModal({
  isOpen,
  onClose,
  workspaceId,
  connector,
  onSaved,
}: ShopifyConnectorModalProps) {
  const isEditing = Boolean(connector);

  // ---- Wizard state (create mode only) ----
  const [step, setStep] = useState<WizardStep>(1);

  // Step 1 — discover
  const [urlInput, setUrlInput] = useState('');
  const [discoveredShopUrl, setDiscoveredShopUrl] = useState<string | null>(null);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState<string | null>(null);

  // Step 2 — scopes & sync config
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set(ALL_SCOPES));
  const [scopeSearch, setScopeSearch] = useState('');
  const [selectedResources, setSelectedResources] = useState<string[]>(['products', 'orders', 'customers']);
  const [frequencyMinutes, setFrequencyMinutes] = useState(1440);
  const [startDate, setStartDate] = useState('');

  // Step 3 — automation
  const [partnersConfigured, setPartnersConfigured] = useState<boolean | null>(null);
  const [partnersEmail, setPartnersEmail] = useState('');
  const [partnersEmailPreview, setPartnersEmailPreview] = useState('');
  const [partnersPassword, setPartnersPassword] = useState('');
  const [partnersDevOrgId, setPartnersDevOrgId] = useState('');
  const [partnersDevOrgIdPreview, setPartnersDevOrgIdPreview] = useState('');
  const [isSavingCreds, setIsSavingCreds] = useState(false);
  const [automationResult, setAutomationResult] = useState<ShopifyAutomateAppCreationResponse | null>(null);
  const [isAutomating, setIsAutomating] = useState(false);
  const [automationError, setAutomationError] = useState<string | null>(null);
  const [liveSteps, setLiveSteps] = useState<LiveStep[]>([]);

  // Browser session state
  const [sessionHasSession, setSessionHasSession] = useState<boolean | null>(null);
  const [sessionLastRefreshed, setSessionLastRefreshed] = useState<string | null>(null);
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const [isRefreshingSession, setIsRefreshingSession] = useState(false);
  const [sessionRefreshStatus, setSessionRefreshStatus] = useState<string | null>(null);
  const [cookieImportOpen, setCookieImportOpen] = useState(false);
  const [cookieJson, setCookieJson] = useState('');
  const [isImportingCookies, setIsImportingCookies] = useState(false);
  const [cookieImportResult, setCookieImportResult] = useState<string | null>(null);
  const [showNoSessionWarning, setShowNoSessionWarning] = useState(false);

  // Step 4 — install links / manual fallback
  const [manualMode, setManualMode] = useState(false);
  const [manualClientId, setManualClientId] = useState('');
  const [manualClientSecret, setManualClientSecret] = useState('');
  const [manualShopUrl, setManualShopUrl] = useState('');
  const [manualInstallUrl, setManualInstallUrl] = useState<string | null>(null);
  const [manualExpiresAt, setManualExpiresAt] = useState<string | null>(null);
  const [isManualSaving, setIsManualSaving] = useState(false);

  // Shared
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // ---- Edit-mode fields ----
  const [editResources, setEditResources] = useState<string[]>([]);
  const [editFrequency, setEditFrequency] = useState(1440);
  const [editStartDate, setEditStartDate] = useState('');

  // ---- Init ----
  useEffect(() => {
    if (!isOpen) return;
    if (connector) {
      setEditResources(connector.resources);
      setEditFrequency(connector.schedule_frequency_minutes);
      setEditStartDate(connector.initial_sync_start_date || '');
    } else {
      resetWizard();
    }
    setError(null);
  }, [connector, isOpen]);

  // ---- Helpers ----

  const resetWizard = () => {
    setStep(1);
    setUrlInput('');
    setDiscoveredShopUrl(null);
    setIsDiscovering(false);
    setDiscoverError(null);
    setSelectedScopes(new Set(ALL_SCOPES));
    setScopeSearch('');
    setSelectedResources(['products', 'orders', 'customers']);
    setFrequencyMinutes(1440);
    setStartDate('');
    setPartnersConfigured(null);
    setPartnersEmail('');
    setPartnersEmailPreview('');
    setPartnersPassword('');
    setPartnersDevOrgId('');
    setPartnersDevOrgIdPreview('');
    setIsSavingCreds(false);
    setAutomationResult(null);
    setIsAutomating(false);
    setAutomationError(null);
    setLiveSteps([]);
    setManualMode(false);
    setManualClientId('');
    setManualClientSecret('');
    setManualShopUrl('');
    setManualInstallUrl(null);
    setManualExpiresAt(null);
    setIsManualSaving(false);
    setSessionHasSession(null);
    setSessionLastRefreshed(null);
    setIsCheckingSession(false);
    setIsRefreshingSession(false);
    setSessionRefreshStatus(null);
    setCookieImportOpen(false);
    setCookieJson('');
    setIsImportingCookies(false);
    setCookieImportResult(null);
    setShowNoSessionWarning(false);
    setCopied(null);
    setError(null);
    setIsSaving(false);
  };

  const handleClose = () => {
    resetWizard();
    onClose();
  };

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  // ---- Step 1: Discover ----

  const handleDiscover = async () => {
    if (!urlInput.trim()) return;
    setIsDiscovering(true);
    setDiscoverError(null);
    setDiscoveredShopUrl(null);
    try {
      const result = await shopifyConnectorApi.discoverShop(urlInput.trim());
      setDiscoveredShopUrl(result.shop_url);
    } catch (err) {
      setDiscoverError(err instanceof Error ? err.message : 'Failed to discover shop URL');
    } finally {
      setIsDiscovering(false);
    }
  };

  // ---- Step 2: Scope helpers ----

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  };

  const toggleAllScopes = () => {
    if (selectedScopes.size === ALL_SCOPES.length) {
      setSelectedScopes(new Set());
    } else {
      setSelectedScopes(new Set(ALL_SCOPES));
    }
  };

  const toggleResource = (value: string) => {
    setSelectedResources((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value]
    );
  };

  const filteredCategories = useMemo(() => {
    if (!scopeSearch.trim()) return SCOPE_CATEGORIES;
    const q = scopeSearch.toLowerCase();
    return SCOPE_CATEGORIES.map((cat) => ({
      ...cat,
      scopes: cat.scopes.filter((s) => s.toLowerCase().includes(q) || cat.name.toLowerCase().includes(q)),
    })).filter((cat) => cat.scopes.length > 0);
  }, [scopeSearch]);

  // ---- Step 3: Partners credentials & automation ----

  const checkPartnersCredentials = useCallback(async () => {
    try {
      const status = await shopifyConnectorApi.getPartnersCredentialsStatus();
      setPartnersConfigured(status.configured);
      setPartnersEmailPreview(status.email_preview || '');
      setPartnersDevOrgIdPreview(status.dev_org_id_preview || '');
    } catch {
      setPartnersConfigured(false);
    }
  }, []);

  const checkSessionStatus = useCallback(async () => {
    setIsCheckingSession(true);
    try {
      const status = await shopifyConnectorApi.getSessionStatus();
      setSessionHasSession(status.has_session);
      setSessionLastRefreshed(status.last_refreshed);
    } catch {
      setSessionHasSession(false);
    } finally {
      setIsCheckingSession(false);
    }
  }, []);

  // Check partners credentials and browser session when entering step 3
  useEffect(() => {
    if (step === 3 && !isEditing) {
      if (partnersConfigured === null) checkPartnersCredentials();
      if (sessionHasSession === null) checkSessionStatus();
    }
  }, [step, isEditing, partnersConfigured, sessionHasSession, checkPartnersCredentials, checkSessionStatus]);

  const handleKeepAlive = async () => {
    setIsRefreshingSession(true);
    setSessionRefreshStatus(null);
    try {
      const result = await shopifyConnectorApi.keepSessionAlive();
      setSessionRefreshStatus(result.status);
      if (result.status === 'active') {
        setSessionHasSession(true);
        setSessionLastRefreshed(new Date().toISOString());
      } else {
        setSessionHasSession(false);
      }
    } catch (err) {
      setSessionRefreshStatus('error');
    } finally {
      setIsRefreshingSession(false);
    }
  };

  const parseTsvCookies = (text: string): Record<string, string>[] => {
    // Chrome DevTools copies cookies from Application > Cookies as whitespace-
    // separated WITHOUT a header row. Observed format from Beni's paste:
    // Name  Value  Domain  Path  Expires  Size  [✓ httpOnly]  [✓ secure]  SameSite  Priority
    // HttpOnly/Secure show as ✓ when true, absent when false.
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) return [];

    // Check if first line looks like a header row
    const firstLower = lines[0].toLowerCase();
    const hasHeader = firstLower.includes('name') && firstLower.includes('value') && firstLower.includes('domain');
    const startIdx = hasHeader ? 1 : 0;

    const CHECKMARKS = ['✓', '✔', '☑'];
    const SAMESITE_VALUES = ['strict', 'lax', 'none'];

    const cookies: Record<string, string>[] = [];
    for (let i = startIdx; i < lines.length; i++) {
      // Split on tab characters; if no tabs found, try splitting on 2+ spaces
      let cols = lines[i].split('\t').map(c => c.trim());
      if (cols.length < 3 && lines[i].includes('  ')) {
        cols = lines[i].split(/  +/).map(c => c.trim());
      }
      if (cols.length < 2) continue;

      // Positional: Name, Value, Domain, Path, Expires, Size, then flags
      const name = cols[0] || '';
      const value = cols[1] || '';
      const domain = cols.length > 2 ? cols[2] : '';
      const path = cols.length > 3 ? cols[3] : '/';
      const expires = cols.length > 4 ? cols[4] : '';
      // cols[5] is usually Size (numeric) — skip

      // Scan remaining cols for checkmarks (✓) and SameSite values
      let httpOnly = false;
      let secure = false;
      let sameSite = '';
      for (let j = 5; j < cols.length; j++) {
        const v = cols[j];
        if (!v) continue;
        if (CHECKMARKS.includes(v)) {
          if (!httpOnly) httpOnly = true;
          else secure = true;
        } else if (SAMESITE_VALUES.includes(v.toLowerCase())) {
          sameSite = v;
        }
        // Skip Size (numeric) and Priority (Low/Medium/High)
      }

      if (name && value) {
        cookies.push({
          name, value, domain, path,
          ...(expires && expires !== 'Session' ? { expires } : {}),
          httpOnly: httpOnly ? 'true' : 'false',
          secure: secure ? 'true' : 'false',
          ...(sameSite ? { sameSite } : {}),
        });
      }
    }
    return cookies;
  };

  const handleImportCookies = async () => {
    if (!cookieJson.trim()) return;
    let parsed: unknown[];
    const trimmed = cookieJson.trim();

    // Detect format: JSON array or tab-separated (from DevTools Application tab)
    if (trimmed.startsWith('[')) {
      // JSON format
      try {
        parsed = JSON.parse(trimmed);
        if (!Array.isArray(parsed)) {
          setCookieImportResult('error: JSON must be an array of cookie objects');
          return;
        }
      } catch {
        setCookieImportResult('error: Invalid JSON — paste the exported cookies array');
        return;
      }
    } else {
      // Try parsing as tab/whitespace-separated (from DevTools Application > Cookies)
      const tsvCookies = parseTsvCookies(trimmed);
      if (tsvCookies.length === 0) {
        setCookieImportResult('error: Could not parse the pasted data. Paste either JSON (from Cookie-Editor) or copy cookies from DevTools → Application → Cookies (Cmd+A, Cmd+C).');
        return;
      }
      // Convert string booleans to actual booleans for the API
      parsed = tsvCookies.map(c => ({
        ...c,
        httpOnly: c.httpOnly === 'true',
        secure: c.secure === 'true',
      }));
    }
    setIsImportingCookies(true);
    setCookieImportResult(null);
    try {
      const result = await shopifyConnectorApi.importBrowserSession(parsed);
      setCookieImportResult(`success: Imported ${result.cookie_count} new cookies (${result.total_cookies} total)`);
      setCookieJson('');
      setSessionHasSession(true);
      setSessionLastRefreshed(new Date().toISOString());
    } catch (err) {
      setCookieImportResult(`error: ${err instanceof Error ? err.message : 'Import failed'}`);
    } finally {
      setIsImportingCookies(false);
    }
  };

  const handleStartAutomationWithCheck = () => {
    if (sessionHasSession === false) {
      setShowNoSessionWarning(true);
      return;
    }
    handleStartAutomation();
  };

  const handleSavePartnersCredentials = async () => {
    if (!partnersEmail.trim() || !partnersPassword.trim() || !partnersDevOrgId.trim()) return;
    setIsSavingCreds(true);
    try {
      await shopifyConnectorApi.storePartnersCredentials(
        partnersEmail.trim(),
        partnersPassword.trim(),
        partnersDevOrgId.trim(),
      );
      setPartnersConfigured(true);
      setPartnersEmailPreview(partnersEmail.trim().charAt(0) + '***@' + partnersEmail.trim().split('@')[1]);
      const orgId = partnersDevOrgId.trim();
      setPartnersDevOrgIdPreview(orgId.length > 4 ? '***' + orgId.slice(-4) : orgId);
      setPartnersEmail('');
      setPartnersPassword('');
      setPartnersDevOrgId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save credentials');
    } finally {
      setIsSavingCreds(false);
    }
  };

  const handleStartAutomation = async () => {
    if (!discoveredShopUrl) return;
    setIsAutomating(true);
    setAutomationError(null);
    setAutomationResult(null);

    // Initialize live steps from step definitions
    const initialSteps: LiveStep[] = AUTOMATION_STEP_DEFS.map((def, i) => ({
      key: def.key,
      label: def.label,
      status: i === 0 ? 'running' : 'pending',
    }));
    setLiveSteps(initialSteps);

    try {
      const result = await shopifyConnectorApi.automateAppCreationStream(
        discoveredShopUrl,
        Array.from(selectedScopes),
        workspaceId,
        (stepEvent) => {
          setLiveSteps((prev) => {
            const updated = prev.map((s) => {
              if (s.key === stepEvent.step) {
                return {
                  ...s,
                  status: (stepEvent.success ? 'success' : 'failed') as StepStatus,
                  detail: stepEvent.detail,
                };
              }
              return s;
            });

            // If the step succeeded, mark the next pending step as running
            if (stepEvent.success) {
              const completedIdx = updated.findIndex((s) => s.key === stepEvent.step);
              for (let i = completedIdx + 1; i < updated.length; i++) {
                if (updated[i].status === 'pending') {
                  updated[i] = { ...updated[i], status: 'running' };
                  break;
                }
              }
            }

            return updated;
          });
        },
        selectedResources,
        frequencyMinutes,
      );

      setAutomationResult(result);
      if (result.success) {
        // Mark all remaining steps as success
        setLiveSteps((prev) =>
          prev.map((s) => (s.status === 'pending' || s.status === 'running' ? { ...s, status: 'success' } : s))
        );
        setStep(4);
      } else {
        setAutomationError(result.error || 'Automation failed');
      }
    } catch (err) {
      // Fallback to non-streaming endpoint
      setLiveSteps([]);
      try {
        const result = await shopifyConnectorApi.automateAppCreation(
          discoveredShopUrl,
          Array.from(selectedScopes),
          workspaceId,
          selectedResources,
          frequencyMinutes,
        );
        setAutomationResult(result);
        if (result.success) {
          setStep(4);
        } else {
          setAutomationError(result.error || 'Automation failed');
        }
      } catch (fallbackErr) {
        setAutomationError(fallbackErr instanceof Error ? fallbackErr.message : 'Automation request failed');
      }
    } finally {
      setIsAutomating(false);
    }
  };

  // ---- Step 4: Manual fallback ----

  const handleManualGenerate = async () => {
    const shopUrl = manualShopUrl.trim() || discoveredShopUrl || '';
    if (!shopUrl || !manualClientId.trim() || !manualClientSecret.trim()) {
      setError('Shop URL, Client ID, and Client Secret are required');
      return;
    }
    if (selectedResources.length === 0) {
      setError('Select at least one resource');
      return;
    }
    setIsManualSaving(true);
    setError(null);
    try {
      const result = await shopifyConnectorApi.generateInstallLink(workspaceId, {
        shop_url: shopUrl,
        client_id: manualClientId.trim(),
        client_secret: manualClientSecret.trim(),
        resources: selectedResources,
        schedule_frequency_minutes: frequencyMinutes,
        initial_sync_start_date: startDate || undefined,
      });
      setManualInstallUrl(result.install_url);
      setManualExpiresAt(result.expires_at);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate install link');
    } finally {
      setIsManualSaving(false);
    }
  };

  // ---- Edit mode save ----

  const handleSaveEdit = async () => {
    if (editResources.length === 0) {
      setError('Select at least one resource');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const updateRequest: ShopifyConnectorUpdateRequest = {
        resources: editResources,
        schedule_frequency_minutes: editFrequency,
        initial_sync_start_date: editStartDate || undefined,
      };
      const saved = await shopifyConnectorApi.update(workspaceId, updateRequest);
      onSaved(saved);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save connector');
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Render ----

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-2xl bg-surface-0 border border-surface-200 shadow-modal rounded-sm p-6 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-success-light flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-success" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-surface-800">
                  {isEditing ? 'Edit Shopify Connector' : 'Connect Shopify'}
                </h2>
                {!isEditing && (
                  <div className="flex items-center gap-2 mt-1">
                    {[1, 2, 3, 4].map((s) => (
                      <div
                        key={s}
                        className={`h-1.5 rounded-full transition-all ${
                          s === step ? 'w-8 bg-teal-500' : s < step ? 'w-4 bg-teal-300' : 'w-4 bg-surface-200'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-surface-100 rounded-sm transition-colors"
            >
              <X className="w-5 h-5 text-surface-500" />
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 rounded-sm bg-error-light border border-error/30 text-error text-sm">
              {error}
            </div>
          )}

          {/* ============================================================== */}
          {/* EDIT MODE — unchanged from original                            */}
          {/* ============================================================== */}
          {isEditing ? (
            <>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-surface-600 mb-2">
                    Resources to sync
                  </label>
                  <div className="space-y-2">
                    {AVAILABLE_RESOURCES.map(({ value, label }) => (
                      <label key={value} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editResources.includes(value)}
                          onChange={() =>
                            setEditResources((prev) =>
                              prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value]
                            )
                          }
                          className="w-4 h-4 rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-surface-800">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-surface-600 mb-1.5">
                    Sync frequency
                  </label>
                  <select
                    value={editFrequency}
                    onChange={(e) => setEditFrequency(Number(e.target.value))}
                    className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50"
                  >
                    {FREQUENCY_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <Input
                  label="Sync data from"
                  type="date"
                  value={editStartDate}
                  onChange={(e) => setEditStartDate(e.target.value)}
                  helperText="Setting an earlier date will backfill older data on the next sync"
                />
              </div>

              <div className="flex gap-3">
                <Button variant="ghost" onClick={handleClose} className="flex-1">Cancel</Button>
                <Button
                  onClick={handleSaveEdit}
                  isLoading={isSaving}
                  disabled={editResources.length === 0}
                  className="flex-1"
                  leftIcon={!isSaving ? <ShoppingBag className="w-4 h-4" /> : undefined}
                >
                  Save Changes
                </Button>
              </div>
            </>
          ) : (
            /* ============================================================ */
            /* CREATE MODE — wizard steps                                   */
            /* ============================================================ */
            <>
              {/* ---- STEP 1: Customer Identification ---- */}
              {step === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-600 mb-1.5">
                      Customer website or Shopify store URL
                    </label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="example.com or example.myshopify.com"
                        value={urlInput}
                        onChange={(e) => {
                          setUrlInput(e.target.value);
                          setDiscoveredShopUrl(null);
                          setDiscoverError(null);
                        }}
                        className="flex-1"
                      />
                      <Button
                        onClick={handleDiscover}
                        isLoading={isDiscovering}
                        disabled={!urlInput.trim()}
                        leftIcon={!isDiscovering ? <Globe className="w-4 h-4" /> : undefined}
                      >
                        Discover
                      </Button>
                    </div>
                    {discoverError && (
                      <p className="mt-2 text-sm text-error">{discoverError}</p>
                    )}
                  </div>

                  {discoveredShopUrl && (
                    <div className="p-4 rounded-sm bg-teal-50 border border-teal-200">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-teal-600" />
                        <span className="text-sm font-medium text-teal-800">
                          Found: {discoveredShopUrl}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={() => setStep(2)}
                      disabled={!discoveredShopUrl}
                      rightIcon={<ArrowRight className="w-4 h-4" />}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}

              {/* ---- STEP 2: Scope & Sync Configuration ---- */}
              {step === 2 && (
                <div className="space-y-4">
                  {/* Scope header */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-surface-600">
                        OAuth Scopes ({selectedScopes.size} of {ALL_SCOPES.length})
                      </label>
                      <button
                        type="button"
                        onClick={toggleAllScopes}
                        className="text-xs font-medium text-teal-600 hover:text-teal-700"
                      >
                        {selectedScopes.size === ALL_SCOPES.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>

                    {/* Search */}
                    <div className="relative mb-3">
                      <Search className="w-4 h-4 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Filter scopes..."
                        value={scopeSearch}
                        onChange={(e) => setScopeSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50"
                      />
                    </div>

                    {/* Scope categories */}
                    <div className="max-h-56 overflow-y-auto border border-surface-200 rounded-sm divide-y divide-surface-100">
                      {filteredCategories.map((cat) => (
                        <div key={cat.name} className="px-3 py-2">
                          <p className="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">
                            {cat.name}
                          </p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {cat.scopes.map((scope) => (
                              <label key={scope} className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedScopes.has(scope)}
                                  onChange={() => toggleScope(scope)}
                                  className="w-3.5 h-3.5 rounded border-surface-300 text-teal-600 focus:ring-teal-500"
                                />
                                <span className="text-xs text-surface-700">{scope}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Resources to sync */}
                  <div>
                    <label className="block text-sm font-medium text-surface-600 mb-2">
                      Resources to sync
                    </label>
                    <div className="space-y-2">
                      {AVAILABLE_RESOURCES.map(({ value, label }) => (
                        <label key={value} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedResources.includes(value)}
                            onChange={() => toggleResource(value)}
                            className="w-4 h-4 rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-surface-800">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Sync frequency */}
                  <div>
                    <label className="block text-sm font-medium text-surface-600 mb-1.5">
                      Sync frequency
                    </label>
                    <select
                      value={frequencyMinutes}
                      onChange={(e) => setFrequencyMinutes(Number(e.target.value))}
                      className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50"
                    >
                      {FREQUENCY_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Start date */}
                  <Input
                    label="Sync data from"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    helperText="Leave empty to sync the last 2 years of data"
                  />

                  {/* Nav */}
                  <div className="flex justify-between pt-2">
                    <Button
                      variant="ghost"
                      onClick={() => setStep(1)}
                      leftIcon={<ArrowLeft className="w-4 h-4" />}
                    >
                      Back
                    </Button>
                    <Button
                      onClick={() => setStep(3)}
                      disabled={selectedScopes.size === 0 || selectedResources.length === 0}
                      rightIcon={<ArrowRight className="w-4 h-4" />}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}

              {/* ---- STEP 3: Automation Progress ---- */}
              {step === 3 && (
                <div className="space-y-4">
                  {/* Partners credentials */}
                  {partnersConfigured === null && (
                    <div className="flex items-center gap-2 text-sm text-surface-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Checking credentials...
                    </div>
                  )}

                  {partnersConfigured === false && (
                    <div className="p-4 rounded-sm border border-surface-200 bg-surface-50 space-y-3">
                      <div className="flex items-center gap-2">
                        <KeyRound className="w-4 h-4 text-surface-500" />
                        <span className="text-sm font-medium text-surface-700">
                          Shopify Partners Credentials
                        </span>
                      </div>
                      <p className="text-xs text-surface-500">
                        Enter credentials for your Shopify Dev Dashboard account. The Org ID is the number in your dev.shopify.com/dashboard/&lt;ID&gt; URL.
                      </p>
                      <Input
                        label="Email"
                        placeholder="partner@example.com"
                        value={partnersEmail}
                        onChange={(e) => setPartnersEmail(e.target.value)}
                      />
                      <Input
                        label="Password"
                        type="password"
                        placeholder="Shopify account password"
                        value={partnersPassword}
                        onChange={(e) => setPartnersPassword(e.target.value)}
                      />
                      <Input
                        label="Dev Dashboard Org ID"
                        placeholder="e.g. 213247144"
                        value={partnersDevOrgId}
                        onChange={(e) => setPartnersDevOrgId(e.target.value)}
                      />
                      <Button
                        size="sm"
                        onClick={handleSavePartnersCredentials}
                        isLoading={isSavingCreds}
                        disabled={!partnersEmail.trim() || !partnersPassword.trim() || !partnersDevOrgId.trim()}
                      >
                        Save Credentials
                      </Button>
                    </div>
                  )}

                  {partnersConfigured && !isAutomating && !automationResult && !automationError && (
                    <div className="space-y-3">
                      <div className="p-3 rounded-sm bg-teal-50 border border-teal-200 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-teal-600" />
                        <span className="text-sm text-teal-800">
                          Credentials configured ({partnersEmailPreview}{partnersDevOrgIdPreview ? `, Org: ${partnersDevOrgIdPreview}` : ''})
                        </span>
                        <button
                          type="button"
                          onClick={() => setPartnersConfigured(false)}
                          className="ml-auto text-xs text-teal-600 hover:text-teal-700 underline"
                        >
                          Update
                        </button>
                      </div>

                      {/* Browser Session Status */}
                      <div className="p-4 rounded-sm border border-surface-200 bg-surface-50 space-y-3">
                        <div className="flex items-center gap-2">
                          <Cookie className="w-4 h-4 text-surface-500" />
                          <span className="text-sm font-medium text-surface-700">Browser Session</span>
                        </div>

                        {isCheckingSession ? (
                          <div className="flex items-center gap-2 text-sm text-surface-500">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Checking session...
                          </div>
                        ) : sessionHasSession ? (
                          <div className="p-3 rounded-sm bg-teal-50 border border-teal-200 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-teal-600 shrink-0" />
                            <span className="text-sm text-teal-800">
                              Browser session active
                              {sessionLastRefreshed && (
                                <span className="text-teal-600"> (last refreshed: {new Date(sessionLastRefreshed).toLocaleString()})</span>
                              )}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleKeepAlive}
                              isLoading={isRefreshingSession}
                              leftIcon={!isRefreshingSession ? <RefreshCw className="w-3.5 h-3.5" /> : undefined}
                              className="ml-auto"
                            >
                              Refresh
                            </Button>
                          </div>
                        ) : (
                          <div className="p-3 rounded-sm bg-amber-50 border border-amber-200 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                            <span className="text-sm text-amber-800">
                              No browser session — automation will require captcha
                            </span>
                          </div>
                        )}

                        {sessionRefreshStatus === 'expired' && (
                          <p className="text-xs text-amber-600">Session expired. Import a fresh session below.</p>
                        )}
                        {sessionRefreshStatus === 'error' && (
                          <p className="text-xs text-error">Failed to refresh session.</p>
                        )}

                        {/* Cookie Import — collapsible */}
                        <div>
                          <button
                            type="button"
                            onClick={() => setCookieImportOpen(!cookieImportOpen)}
                            className="flex items-center gap-1.5 text-sm font-medium text-surface-600 hover:text-surface-800 transition-colors"
                          >
                            {cookieImportOpen ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                            Import Browser Session
                          </button>

                          {cookieImportOpen && (
                            <div className="mt-3 space-y-3">
                              <p className="text-xs font-medium text-surface-600 mb-1">How to export cookies (pick one):</p>

                              <div className="space-y-2 mb-2">
                                <div className="p-2 rounded-sm bg-surface-100 border border-surface-200">
                                  <p className="text-xs font-medium text-surface-700 mb-1">DevTools (no extension needed)</p>
                                  <ol className="text-xs text-surface-500 space-y-0.5 list-decimal list-inside">
                                    <li>Log in to <strong>accounts.shopify.com</strong> AND <strong>partners.shopify.com</strong></li>
                                    <li>Open DevTools (F12) → <strong>Application</strong> tab → <strong>Cookies</strong> in sidebar</li>
                                    <li>Click any cookie row → <strong>Cmd+A</strong> (select all) → <strong>Cmd+C</strong> (copy)</li>
                                    <li>Repeat for <strong>partners.shopify.com</strong> cookies (needed for distribution links)</li>
                                    <li>Paste below (tab-separated format is auto-detected)</li>
                                  </ol>
                                </div>

                                <div className="p-2 rounded-sm bg-surface-100 border border-surface-200">
                                  <p className="text-xs font-medium text-surface-700 mb-1">Cookie-Editor extension</p>
                                  <ol className="text-xs text-surface-500 space-y-0.5 list-decimal list-inside">
                                    <li>Install <a href="https://chromewebstore.google.com/detail/cookie-editor/hlkenndednhfkekhgcdicdfddnkalmdm" target="_blank" rel="noopener noreferrer" className="text-teal-600 underline">Cookie-Editor</a> for Chrome</li>
                                    <li>Export from <strong>accounts.shopify.com</strong>, then also from <strong>partners.shopify.com</strong></li>
                                    <li>Click icon → <strong>Export</strong> → paste below (JSON format)</li>
                                  </ol>
                                </div>
                              </div>

                              <textarea
                                value={cookieJson}
                                onChange={(e) => {
                                  setCookieJson(e.target.value);
                                  setCookieImportResult(null);
                                }}
                                placeholder='Paste JSON array or tab-separated cookies from DevTools here...'
                                rows={4}
                                className="w-full px-3 py-2 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-xs font-mono resize-y focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50"
                              />

                              {cookieImportResult && (
                                <p className={`text-xs ${cookieImportResult.startsWith('success') ? 'text-teal-600' : 'text-error'}`}>
                                  {cookieImportResult.replace(/^(success|error): /, '')}
                                </p>
                              )}

                              <Button
                                size="sm"
                                onClick={handleImportCookies}
                                isLoading={isImportingCookies}
                                disabled={!cookieJson.trim()}
                                leftIcon={!isImportingCookies ? <Upload className="w-3.5 h-3.5" /> : undefined}
                              >
                                Import Session
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>

                      <p className="text-sm text-surface-600">
                        Ready to create a Shopify app for <strong>{discoveredShopUrl}</strong> with {selectedScopes.size} scopes.
                      </p>

                      <Button
                        onClick={handleStartAutomationWithCheck}
                        leftIcon={<ShoppingBag className="w-4 h-4" />}
                      >
                        Start Automation
                      </Button>

                      {/* No-session warning dialog */}
                      {showNoSessionWarning && (
                        <div className="p-3 rounded-sm bg-amber-50 border border-amber-200 space-y-3">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                            <span className="text-sm font-medium text-amber-800">No browser session found</span>
                          </div>
                          <p className="text-xs text-amber-700">
                            Automation may fail due to captcha. Import your browser session first or proceed anyway.
                          </p>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowNoSessionWarning(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setShowNoSessionWarning(false);
                                handleStartAutomation();
                              }}
                            >
                              Proceed Anyway
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Automation in progress */}
                  {isAutomating && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-surface-600">
                        <Loader2 className="w-4 h-4 animate-spin text-teal-500" />
                        Creating Shopify app... This may take a minute.
                      </div>
                      <AutomationSteps steps={[]} liveSteps={liveSteps} running />
                    </div>
                  )}

                  {/* Automation completed with steps */}
                  {automationResult && !automationResult.success && (
                    <div className="space-y-3">
                      <AutomationSteps steps={automationResult.steps} liveSteps={liveSteps} />
                      <div className="p-3 rounded-sm bg-error-light border border-error/30">
                        <div className="flex items-center gap-2 mb-1">
                          <XCircle className="w-4 h-4 text-error" />
                          <span className="text-sm font-medium text-error">Automation failed</span>
                        </div>
                        <p className="text-xs text-error/80">
                          {automationError || automationResult.error}
                          {automationResult.error_step && (
                            <> (at step: {automationResult.error_step})</>
                          )}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setManualMode(true);
                            setManualShopUrl(discoveredShopUrl || '');
                            setStep(4);
                          }}
                        >
                          Switch to Manual Setup
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setAutomationResult(null);
                            setAutomationError(null);
                            setLiveSteps([]);
                          }}
                        >
                          Retry
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Automation error without result */}
                  {automationError && !automationResult && !isAutomating && (
                    <div className="space-y-3">
                      <div className="p-3 rounded-sm bg-error-light border border-error/30">
                        <div className="flex items-center gap-2 mb-1">
                          <XCircle className="w-4 h-4 text-error" />
                          <span className="text-sm font-medium text-error">Request failed</span>
                        </div>
                        <p className="text-xs text-error/80">{automationError}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setManualMode(true);
                            setManualShopUrl(discoveredShopUrl || '');
                            setStep(4);
                          }}
                        >
                          Switch to Manual Setup
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setAutomationError(null)}
                        >
                          Retry
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Nav */}
                  <div className="flex justify-between pt-2">
                    <Button
                      variant="ghost"
                      onClick={() => setStep(2)}
                      leftIcon={<ArrowLeft className="w-4 h-4" />}
                      disabled={isAutomating}
                    >
                      Back
                    </Button>
                  </div>
                </div>
              )}

              {/* ---- STEP 4: Install Links / Manual Fallback ---- */}
              {step === 4 && (
                <div className="space-y-4">
                  {!manualMode && automationResult?.success ? (
                    /* Automation success — show links */
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {automationResult.app_url && (
                          <LinkCard
                            title="Shopify Install Link"
                            description="Send this first. The customer installs the app on their store."
                            url={automationResult.app_url}
                            copied={copied === 'app'}
                            onCopy={() => copyToClipboard(automationResult.app_url!, 'app')}
                          />
                        )}
                        {automationResult.our_install_url && (
                          <LinkCard
                            title="OAuth Authorization Link"
                            description="Send this second. Triggers the callback to register in our backend."
                            url={automationResult.our_install_url}
                            copied={copied === 'oauth'}
                            onCopy={() => copyToClipboard(automationResult.our_install_url!, 'oauth')}
                          />
                        )}
                      </div>

                      <p className="text-xs text-surface-500">
                        The connector will activate automatically once the customer completes the OAuth flow.
                      </p>

                      <div className="flex gap-3 pt-2">
                        <Button
                          variant="ghost"
                          onClick={() => {
                            resetWizard();
                            setStep(1);
                          }}
                        >
                          Start Over
                        </Button>
                        <Button onClick={() => { onSaved({} as ShopifyConnector); handleClose(); }} className="flex-1">
                          Done
                        </Button>
                      </div>
                    </>
                  ) : (
                    /* Manual fallback */
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-surface-700">Manual Setup</h3>
                        {manualMode && (
                          <button
                            type="button"
                            onClick={() => {
                              setManualMode(false);
                              setStep(3);
                              setAutomationResult(null);
                              setAutomationError(null);
                            }}
                            className="text-xs text-teal-600 hover:text-teal-700 underline"
                          >
                            Back to Automated Setup
                          </button>
                        )}
                      </div>

                      {!manualInstallUrl ? (
                        <div className="space-y-4">
                          <Input
                            label="Shop URL"
                            placeholder="your-store.myshopify.com"
                            value={manualShopUrl}
                            onChange={(e) => setManualShopUrl(e.target.value)}
                            helperText="The customer's Shopify store domain"
                          />
                          <Input
                            label="Client ID"
                            placeholder="Shopify app client ID"
                            value={manualClientId}
                            onChange={(e) => setManualClientId(e.target.value)}
                            helperText="From the Shopify app dashboard (dev.shopify.com)"
                          />
                          <Input
                            label="Client Secret"
                            placeholder="Shopify app client secret"
                            type="password"
                            value={manualClientSecret}
                            onChange={(e) => setManualClientSecret(e.target.value)}
                          />

                          <div className="flex gap-3">
                            <Button variant="ghost" onClick={handleClose} className="flex-1">
                              Cancel
                            </Button>
                            <Button
                              onClick={handleManualGenerate}
                              isLoading={isManualSaving}
                              className="flex-1"
                            >
                              Generate Install Link
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <LinkCard
                            title="Install Link"
                            description={
                              manualExpiresAt
                                ? `Expires at ${new Date(manualExpiresAt).toLocaleString()}`
                                : 'Send this link to the customer to authorize access.'
                            }
                            url={manualInstallUrl}
                            copied={copied === 'manual'}
                            onCopy={() => copyToClipboard(manualInstallUrl!, 'manual')}
                          />
                          <p className="text-xs text-surface-500">
                            The connector will be created automatically once the customer completes the Shopify authorization.
                          </p>
                          <Button onClick={handleClose} className="w-full">Done</Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AutomationSteps({
  steps,
  liveSteps,
  running,
}: {
  steps: ShopifyAutomationStepResult[];
  liveSteps?: LiveStep[];
  running?: boolean;
}) {
  // If we have live steps (streaming mode), use those
  if (liveSteps && liveSteps.length > 0) {
    return (
      <div className="space-y-1.5">
        {liveSteps.map((s) => (
          <div key={s.key} className="flex items-center gap-2 text-sm">
            {s.status === 'success' ? (
              <CheckCircle className="w-4 h-4 text-teal-500 shrink-0" />
            ) : s.status === 'failed' ? (
              <XCircle className="w-4 h-4 text-error shrink-0" />
            ) : s.status === 'running' ? (
              <Loader2 className="w-4 h-4 text-teal-500 animate-spin shrink-0" />
            ) : (
              <div className="w-4 h-4 rounded-full border border-surface-300 shrink-0" />
            )}
            <span
              className={
                s.status === 'success'
                  ? 'text-surface-700'
                  : s.status === 'failed'
                    ? 'text-error'
                    : s.status === 'running'
                      ? 'text-surface-700'
                      : 'text-surface-500'
              }
            >
              {s.label}
            </span>
            {s.detail && <span className="text-xs text-surface-400 ml-1">— {s.detail}</span>}
          </div>
        ))}
      </div>
    );
  }

  // Legacy mode: use steps array from non-streaming response
  const STEP_NAMES = [
    'Logging in',
    'Creating app',
    'Configuring & publishing',
    'Extracting credentials',
    'Generating install link',
  ];

  const displaySteps = steps.length > 0
    ? steps
    : STEP_NAMES.map((name) => ({ step: name, status: 'pending' as const, detail: undefined }));

  return (
    <div className="space-y-1.5">
      {displaySteps.map((s, i) => {
        const name = s.step || STEP_NAMES[i] || `Step ${i + 1}`;
        const isPending = running && s.status === 'pending';
        const isRunningStep = running && i === 0 && steps.length === 0;

        return (
          <div key={i} className="flex items-center gap-2 text-sm">
            {s.status === 'success' ? (
              <CheckCircle className="w-4 h-4 text-teal-500 shrink-0" />
            ) : s.status === 'failed' ? (
              <XCircle className="w-4 h-4 text-error shrink-0" />
            ) : isPending || isRunningStep ? (
              <Loader2 className="w-4 h-4 text-surface-400 animate-spin shrink-0" />
            ) : (
              <div className="w-4 h-4 rounded-full border border-surface-300 shrink-0" />
            )}
            <span
              className={
                s.status === 'success'
                  ? 'text-surface-700'
                  : s.status === 'failed'
                    ? 'text-error'
                    : 'text-surface-500'
              }
            >
              {name}
            </span>
            {s.detail && <span className="text-xs text-surface-400 ml-1">— {s.detail}</span>}
          </div>
        );
      })}
    </div>
  );
}

function LinkCard({
  title,
  description,
  url,
  copied,
  onCopy,
}: {
  title: string;
  description: string;
  url: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="p-4 rounded-sm border border-surface-200 bg-surface-50 space-y-2">
      <div className="flex items-center gap-2">
        <ExternalLink className="w-4 h-4 text-teal-600" />
        <span className="text-sm font-medium text-surface-800">{title}</span>
      </div>
      <p className="text-xs text-surface-500">{description}</p>
      <div className="flex gap-2">
        <input
          readOnly
          value={url}
          className="flex-1 px-3 py-2 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-xs font-mono truncate"
          onFocus={(e) => e.target.select()}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={onCopy}
          leftIcon={copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

export default ShopifyConnectorModal;
